import axios from 'axios'

export type SavedOrderStatus = 'draft' | 'finalized'
export type SavedOrderPriority = 'high' | 'medium' | 'low'
export type SavedOrderGenerationOrigin = 'model' | 'seller' | 'lojista'
export type AdjustmentActorType = 'seller' | 'lojista'
export type SavedOrderAuditAction =
  | 'created'
  | 'quantity_changed'
  | 'removed'
  | 'restored'
  | 'finalized'

export interface SavedPurchaseOrder {
  order_id: number
  order_number: string
  id_lojista: string
  cnpj: string
  razao_social?: string | null
  status: SavedOrderStatus
  source: 'automatic'
  is_modified: boolean
  generation_origin: SavedOrderGenerationOrigin
  version: number
  created_by?: number | null
  updated_by?: number | null
  finalized_by?: number | null
  created_by_name?: string | null
  updated_by_name?: string | null
  finalized_by_name?: string | null
  source_calculated_at: string
  created_at: string
  updated_at: string
  finalized_at?: string | null
  item_count: number
  total_units: number
  original_total_units: number
  modified_items: number
  has_manual_changes: boolean
  manual_adjustment_count: number
  has_seller_changes: boolean
  has_retailer_changes: boolean
  last_manual_change_at?: string | null
}

export interface SavedPurchaseOrderItem {
  item_id: number
  order_id: number
  ean: string
  product_name: string
  foto?: string | null
  fornecedor?: string | null
  classification_snapshot?: string | null
  priority_snapshot: SavedOrderPriority
  suggested_quantity: number
  final_quantity: number
  stock_snapshot: number
  cmd_snapshot: number
  ss_snapshot: number
  rop_snapshot: number
  coverage_snapshot: number
  target_stock_snapshot: number
  lead_time_snapshot: number
  removed_by?: number | null
  removed_at?: string | null
  is_removed: boolean
  created_at: string
  updated_at: string
}

export interface SavedPurchaseOrderAudit {
  audit_id: number
  order_id: number
  item_id?: number | null
  adjustment_id?: number | null
  action: SavedOrderAuditAction
  field_name?: string | null
  old_value?: string | null
  new_value?: string | null
  changed_by?: number | null
  changed_by_name?: string | null
  changed_at: string
  product_name?: string | null
  ean?: string | null
  actor_type?: AdjustmentActorType | null
  reason_code?: string | null
  reason_label?: string | null
  reason_text?: string | null
  suggested_quantity?: number
  final_quantity?: number
}

export interface SavedPurchaseOrderAdjustment {
  adjustment_id: number
  order_id: number
  order_version: number
  actor_type: AdjustmentActorType
  reason_code: string
  reason_label: string
  reason_text?: string | null
  changed_by?: number | null
  changed_by_name?: string | null
  created_at: string
}

export interface SavedPurchaseOrderDetail {
  order: SavedPurchaseOrder
  items: SavedPurchaseOrderItem[]
  audit: SavedPurchaseOrderAudit[]
  adjustments: SavedPurchaseOrderAdjustment[]
}

export interface ModifiedPurchaseOrderHistory {
  order_id: number
  order_number: string
  cnpj: string
  razao_social?: string | null
  status: SavedOrderStatus
  generation_origin: SavedOrderGenerationOrigin
  is_modified: boolean
  created_at: string
  updated_at: string
  changes: SavedPurchaseOrderAudit[]
}

interface ApiEnvelope<T> {
  success: boolean
  data: T
  error?: string
  code?: string
}

export class PurchaseOrderApiError extends Error {
  code?: string

  constructor(message: string, code?: string) {
    super(message)
    this.name = 'PurchaseOrderApiError'
    this.code = code
  }
}

function rethrowApiError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as {error?: string; code?: string} | undefined
    throw new PurchaseOrderApiError(
      payload?.error || 'Não foi possível concluir a operação.',
      payload?.code
    )
  }
  if (error instanceof Error) throw error
  throw new PurchaseOrderApiError('Não foi possível concluir a operação.')
}

export async function fetchSavedPurchaseOrders(
  status?: SavedOrderStatus
): Promise<SavedPurchaseOrder[]> {
  try {
    const response = await axios.get<ApiEnvelope<SavedPurchaseOrder[]>>(
      '/sellers/purchase-orders',
      {params: status ? {status} : undefined}
    )
    return response.data.data || []
  } catch (error) {
    return rethrowApiError(error)
  }
}

export async function fetchModifiedPurchaseOrderHistory(): Promise<ModifiedPurchaseOrderHistory[]> {
  try {
    const response = await axios.get<ApiEnvelope<ModifiedPurchaseOrderHistory[]>>(
      '/sellers/purchase-orders/audit/modified'
    )
    return response.data.data || []
  } catch (error) {
    return rethrowApiError(error)
  }
}

export async function fetchSavedPurchaseOrder(
  orderId: number
): Promise<SavedPurchaseOrderDetail> {
  try {
    const response = await axios.get<ApiEnvelope<SavedPurchaseOrderDetail>>(
      `/sellers/purchase-orders/${orderId}`
    )
    return response.data.data
  } catch (error) {
    return rethrowApiError(error)
  }
}

export async function createPreOrder(input: {
  cnpj: string
  razao_social?: string
}): Promise<SavedPurchaseOrderDetail> {
  try {
    const response = await axios.post<ApiEnvelope<SavedPurchaseOrderDetail>>(
      '/sellers/purchase-orders',
      input
    )
    return response.data.data
  } catch (error) {
    return rethrowApiError(error)
  }
}

export async function updatePreOrder(
  orderId: number,
  input: {
    version: number
    items: Array<{item_id: number; quantity: number}>
    adjustment_reason_code: string
    adjustment_reason_text?: string
  }
): Promise<SavedPurchaseOrderDetail> {
  try {
    const response = await axios.patch<ApiEnvelope<SavedPurchaseOrderDetail>>(
      `/sellers/purchase-orders/${orderId}`,
      input
    )
    return response.data.data
  } catch (error) {
    return rethrowApiError(error)
  }
}

export async function finalizePurchaseOrder(
  orderId: number,
  version: number
): Promise<SavedPurchaseOrderDetail> {
  try {
    const response = await axios.post<ApiEnvelope<SavedPurchaseOrderDetail>>(
      `/sellers/purchase-orders/${orderId}/finalize`,
      {version}
    )
    return response.data.data
  } catch (error) {
    return rethrowApiError(error)
  }
}
